import { lazy, Suspense } from "react";
import { useAuthContext } from "providers/Auth";
import { Link, useNavigate } from "react-router-dom";
import { NavLink } from "./NavLink";
import { boomScreenLinks, boomReportLinks, Links } from "../constants";
import { Check } from "untitledui-js-base";
import { Menu } from "./Menu";
import { useProgressContext } from "providers/ProgressContext";
import { Loader } from "shared/components/Loader";
import { CsvUploadStatus } from "providers/ProgressContext/slices/UploadCsvSlice/types";
import { useCountMetaRentReportingController } from "views/rent-reporting/controllers/general-meta-controller";
import { cx } from "shared/utils";
import logoSquare from "shared/branding/logo-square.svg";
import { IconButton, IconButtonVariant } from "shared/components/IconButton";
import { shouldShowReportingLinks } from "../utils";
import { PartnerFeatures } from "shared/types";
import { FEATURES } from "types/landlord/partner";
import { IconButtonSize, BadgeVariant } from "shared/components";
import {
  Calendar04Icon,
  Cancel01Icon,
  FilterIcon,
  LaptopSettingsIcon,
  SquareLock02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { GlobalLinkSection } from "./GlobalLinksSection";
import { NewBadge } from "shared/app/NewBadge";

const Notifications = lazy(() => import("./Notifications"));

const sectionHeaderClassName = cx(
  "mb-2 ml-3 hidden text-xs font-medium text-content-200",
  "md:group-hover:block"
);

const sectionHeaderWithBadgeClassName = cx(
  "mb-2 ml-3 hidden items-center gap-2 text-xs font-medium text-content-200",
  "md:group-hover:flex"
);

const BoomScreenNavSection = () => {
  const { type, permissions, partnerFeatures, auth } = useAuthContext();
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);

  if (!type.isBoomScreen) return null;

  return (
    <div className="hideScroll mb-3 px-4">
      <p className={sectionHeaderClassName}>BoomScreen</p>
      {boomScreenLinks
        .filter((link) => {
          if (link.name === Links.SCREENING_OUTCOMES)
            return type.isBoomScreenOutcomes && type.adminPrivileges;

          // Hide screening settings for PMs without any screening permissions
          if (type.isPropertyManager && link.name === Links.SCREENING_SETTINGS)
            // Show if they have either permission
            return (
              auth?.can_access_screening_templates ||
              permissions.canManageDiscounts
            );

          return true;
        })
        .map(({ to, name, Icon }) => {
          const linkTo =
            name === Links.SCREENING_SETTINGS &&
            type.isPropertyManager &&
            !auth?.can_access_screening_templates &&
            permissions.canManageDiscounts
              ? "/discounts"
              : to;

          return (
            <NavLink to={linkTo} HugeIcon={Icon} key={"boomscreen" + to + name}>
              {name}
            </NavLink>
          );
        })}
      {hasChat ? (
        <Suspense fallback={null}>
          <Notifications />
        </Suspense>
      ) : null}
    </div>
  );
};

const RentReportingNavSection = () => {
  const { type, partner, permissions, pms } = useAuthContext();
  const { needToReportCountMeta, pendingCountMeta } =
    useCountMetaRentReportingController();
  const hasIdentityTheftProtection = partner?.features.includes(
    FEATURES.ENABLE_IDENTITY_THEFT_PROTECTION
  );

  if (!type.isRentReporting && !type.isSDK) return null;

  return (
    <div className="mb-6 border-solid border-alpha-40 px-4">
      <p className={sectionHeaderClassName}>
        {hasIdentityTheftProtection ? "Resident benefits" : "BoomReport"}
      </p>

      {boomReportLinks
        .filter(
          ({ name, permission }) =>
            shouldShowReportingLinks(name, type, partner, pms) &&
            permissions[permission as keyof typeof permissions] &&
            (name !== Links.IDENTITY_THEFT_PROTECTION ||
              hasIdentityTheftProtection)
        )
        .map(({ to, name, Icon, strokeWidth }) => (
          <NavLink
            to={to}
            HugeIcon={Icon}
            strokeWidth={strokeWidth}
            key={"boomreport" + to + name}
          >
            <div className="flex gap-2 items-center">
              {name}{" "}
              {name === Links.RENT_REPORTING &&
                ((type.isSDK
                  ? pendingCountMeta.data
                  : needToReportCountMeta.data) || 0) > 0 && (
                  <div className="flex items-center justify-center rounded bg-danger-500 text-xxs text-background-1000 px-2">
                    {type.isSDK
                      ? pendingCountMeta.data
                      : needToReportCountMeta.data}
                  </div>
                )}
            </div>
          </NavLink>
        ))}
    </div>
  );
};

const SidebarStatusBanner = () => {
  const navigate = useNavigate();
  const { subscriptionsQueue, uploadCsv } = useProgressContext();

  if (subscriptionsQueue.isSyncing)
    return (
      <div className="pl-4 pr-6">
        <div className="flex items-center gap-3 overflow-x-hidden p-3">
          <div className="shrink-0">
            <Loader size={20} />
          </div>
          <p className="text-xs text-content-500">
            Subscriptions queued: {subscriptionsQueue.amount}
          </p>
        </div>
      </div>
    );

  if (uploadCsv.status === CsvUploadStatus.Parsing)
    return (
      <Link to="/rent-reporting/upload">
        <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
          <div className="flex items-center gap-3 overflow-x-hidden p-3">
            <div className="mt-1 shrink-0 self-start">
              <Loader size={20} />
            </div>
            <p className="text-xs text-content-500">
              Analyzing rent reporting CSV file...
            </p>
          </div>
        </div>
      </Link>
    );

  if (uploadCsv.status === CsvUploadStatus.Parsed)
    return (
      <Link to="/rent-reporting/upload">
        <div className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6">
          <div className="flex items-center gap-3 overflow-x-hidden p-3">
            <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
              <Check size="14px" className="stroke-white stroke-[2px]" />
            </div>
            <p className="text-xs text-success-800">
              Rent reporting CSV file analysis ready to review
            </p>
          </div>
        </div>
      </Link>
    );

  if (uploadCsv.status === CsvUploadStatus.Executing)
    return (
      <Link to="/rent-reporting/upload">
        <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
          <div className="flex items-center gap-3 overflow-x-hidden p-3">
            <div className="mt-1 shrink-0 self-start">
              <Loader size={20} />
            </div>
            <p className="text-xs text-content-500">
              Processing rent reporting CSV file...
            </p>
          </div>
        </div>
      </Link>
    );

  if (uploadCsv.status === CsvUploadStatus.Executed)
    return (
      <div
        className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6 cursor-pointer"
        onClick={() => {
          navigate("/rent-reporting/upload", {
            state: {
              review: true
            }
          });
        }}
      >
        <div className="flex items-center gap-3 overflow-x-hidden p-3">
          <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
            <Check size="14px" className="stroke-white stroke-[2px]" />
          </div>
          <p className="text-xs text-success-800">
            Rent reporting CSV file processed successfully
          </p>
        </div>
      </div>
    );

  return null;
};

interface AsideProps {
  isOpenFromMobile: boolean;
  setIsOpenFromMobile: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Aside = ({
  isOpenFromMobile,
  setIsOpenFromMobile
}: AsideProps) => {
  const { type } = useAuthContext();

  return (
    <aside
      className={cx(
        isOpenFromMobile
          ? "w-[240px] left-0"
          : "w-[80px] left-[-240px] md:left-0",
        "group flex no-print transition-all top-0 absolute z-[14] h-screen flex-col border-r border-alpha-100 bg-white md:hover:w-[240px]"
      )}
    >
      <div className="flex shrink-0">
        {isOpenFromMobile ? (
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            className="!text-content-200 my-6 ml-6"
            onClick={() => setIsOpenFromMobile(false)}
          />
        ) : (
          <Link to="/" className="mb-8 ml-6 mt-4 block">
            <img
              src={logoSquare}
              className="h-[30px] w-[30px] hover:rotate-[360deg] transition-all duration-300 ease-in-out rounded-full"
            />
          </Link>
        )}
      </div>
      <div className="hide-scroll flex grow flex-col overflow-y-auto">
        <nav className="flex h-full flex-col relative">
          <GlobalLinkSection />

          {type.isBoomCRM ? (
            <div className="hideScroll mb-3 pl-4 pr-5">
              <div className={sectionHeaderWithBadgeClassName}>
                <p>BoomCRM</p>
                <NewBadge variant={BadgeVariant.BASE} />
              </div>
              <NavLink to="/leads" HugeIcon={FilterIcon}>
                Leads
              </NavLink>
              <NavLink to="/listing-settings" HugeIcon={LaptopSettingsIcon}>
                Listing settings
              </NavLink>
              {type.isLockboxes ? (
                <NavLink to="/lockboxes" HugeIcon={SquareLock02Icon}>
                  Lockboxes
                </NavLink>
              ) : null}
              {type.isShowings ? (
                <NavLink to="/showings" HugeIcon={Calendar04Icon}>
                  Showings
                </NavLink>
              ) : null}
            </div>
          ) : null}
          <BoomScreenNavSection />
          <RentReportingNavSection />
        </nav>
      </div>
      <div className="flex shrink-0 flex-col justify-end">
        <SidebarStatusBanner />

        <Menu />
      </div>
    </aside>
  );
};
